import dashboardImg from "@/assets/cases/crm-medico-dashboard.png";
import wireframeImg from "@/assets/cases/crm-medico-wireframe.png";
import examesImg from "@/assets/cases/crm-medico-exames.png";
import pacientesImg from "@/assets/cases/crm-medico-pacientes.png";
import safraMetricasImg from "@/assets/cases/safra-metricas.png";
import safraAntesDepoisImg from "@/assets/cases/safra-antes-depois.png";
import safraOverviewImg from "@/assets/cases/safra-overview.png";
import safraTransbordoImg from "@/assets/cases/safra-transbordo.png";
import safraFluxogramaImg from "@/assets/cases/safra-fluxograma.png";
import agzeroHeroImg from "@/assets/cases/agzero-hero.png";
import agzeroIntencoesImg from "@/assets/cases/agzero-intencoes.png";
import agzeroMenuImg from "@/assets/cases/agzero-menu.png";
import agzeroAntesDepoisImg from "@/assets/cases/agzero-antes-depois.png";
import agzeroMinhaContaImg from "@/assets/cases/agzero-minha-conta.png";
import agzeroEncerramentoImg from "@/assets/cases/agzero-encerramento.png";
import agzeroPagamentoImg from "@/assets/cases/agzero-pagamento.png";
import agzeroServicosImg from "@/assets/cases/agzero-servicos.png";
import agzeroFluxogramaImg from "@/assets/cases/agzero-fluxograma.png";

export interface CaseStudy {
  id: string;
  title: string;
  slug: string;
  company: string;
  role: string;
  category: string;
  description: string;
  image: string;
  tags: string[];
  details: {
    challenge: string;
    solution: string;
    results: string[];
    responsibilities: string[];
    tools: string[];
    keyLearning: string;
    figmaLink?: string;
    prototypeLink?: string;
    images: {
      src: string;
      caption: string;
    }[];
  };
}

export const cases: CaseStudy[] = [
  {
    id: "1",
    title: "CRM Médico com Apoio de IA",
    slug: "crm-medico-ia",
    company: "Projeto B2B",
    role: "Product Lead",
    category: "UX/UI Design",
    description: "Sistema CRM especializado para hospitais e clínicas com integração de chatbot inteligente e monitoramento em tempo real.",
    image: dashboardImg,
    tags: ["UX Design", "IA", "Chatbot", "Healthcare", "B2B"],
    details: {
      challenge: "A falta de CRMs adequados e acessíveis para hospitais e clínicas, levando ao uso de soluções genéricas ou caras e com funcionalidades limitadas.",
      solution: "Desenvolvi um CRM especializado focado nas necessidades específicas do setor de saúde, com interface clean e minimalista, integração de chatbot com IA para atendimento automatizado, e sistema de monitoramento em tempo real das interações.",
      results: [
        "Interface otimizada para leitura rápida e escaneabilidade",
        "Redução no tempo de busca de informações críticas",
        "Chatbot integrado com OpenAI para suporte 24/7",
        "Sistema de monitoramento para melhoria contínua"
      ],
      responsibilities: [
        "Criação e prototipação completa",
        "Design de interface e experiência",
        "UX Writing estratégico",
        "Criação de normas do sistema de bot",
        "Simulação de integrações complexas"
      ],
      tools: ["Figma", "Claude", "Framer", "Botpress", "OpenAI API", "Kibana"],
      keyLearning: "A sinergia entre IA e conhecimento de UX foi crucial para acelerar o desenvolvimento e refinar a precisão das integrações. Um design de conteúdo dinâmico, adaptando padrões de leitura para cada contexto de página, provou-se essencial para a busca eficiente de informações em um ambiente de ritmo acelerado.",
      figmaLink: "https://www.figma.com/design/X4B3CdN69LWMwENUey7kIb/Fluxograma-CRM-V1?node-id=21-2&t=Fh6aVdrvgCm0Kx1e-1",
      prototypeLink: "https://testecrmdopaulo.framer.website/mensagens",
      images: [
        {
          src: dashboardImg,
          caption: "Dashboard principal com painel de controle e agenda do dia"
        },
        {
          src: wireframeImg,
          caption: "Wireframe de alta fidelidade mostrando a estrutura da interface"
        },
        {
          src: examesImg,
          caption: "Tela de gestão de exames com categorização e priorização"
        },
        {
          src: pacientesImg,
          caption: "Lista de pacientes com status de saúde e informações detalhadas"
        }
      ]
    }
  },
  {
    id: "2",
    title: "Transformação Digital no Atendimento Bancário",
    slug: "transformacao-safra",
    company: "Take Blip / Banco Safra",
    role: "UX Writer",
    category: "Chatbot",
    description: "Processo de transformação digital do atendimento, migrando de URA para chatbots in-app e WhatsApp, focado no público PJ.",
    image: safraMetricasImg,
    tags: ["Chatbot", "UX Writing", "B2B", "Banking", "WhatsApp"],
    details: {
      challenge: "O Banco Safra enfrentava altas taxas de reclamações no Reclame Aqui, baixo NPS, engajamento reduzido com o chatbot e abandono frequente durante interações. Havia excesso de transferências para atendimento humano e baixa assertividade em resoluções simples.",
      solution: "Realizamos uma auditoria completa do bot existente, seguida de uma reengenharia inteligente focada em recategorização de menus, aproximação da linguagem bancária ao usuário, otimização de fluxos e nova arquitetura de informação. Priorizamos a reorganização de menus por relevância e destaque para serviços mais acessados.",
      results: [
        "26% de aumento na taxa de retenção",
        "34% de redução na taxa de abandono",
        "Melhoria significativa na resolutividade do bot",
        "Redução de custos operacionais",
        "Sistema de métricas mais robusto para análises futuras"
      ],
      responsibilities: [
        "Criação de todo o fluxo conversacional focado no público PJ",
        "Realização de pesquisas de campo",
        "Execução de testes A/B",
        "Auditoria e análise de dados comportamentais",
        "Reengenharia da arquitetura de informação"
      ],
      tools: ["Take Blip", "Kibana", "Google Analytics", "Figma", "NLP"],
      keyLearning: "A importância do equilíbrio entre automação e toque humano, o valor da simplicidade na experiência do usuário e a necessidade de métricas claras para evolução contínua. O sucesso não está apenas nos números, mas na criação de uma experiência que verdadeiramente atende às necessidades dos clientes.",
      images: [
        {
          src: safraMetricasImg,
          caption: "Métricas de assertividade, retenção e satisfação NPS após otimizações"
        },
        {
          src: safraAntesDepoisImg,
          caption: "Comparação entre a experiência antes e depois da transformação"
        },
        {
          src: safraOverviewImg,
          caption: "Overview completo com heatmap de mensagens e métricas de engajamento"
        },
        {
          src: safraTransbordoImg,
          caption: "Dados de transbordo mostrando redução significativa nas transferências"
        },
        {
          src: safraFluxogramaImg,
          caption: "Fluxograma da pesquisa de satisfação integrada ao chatbot"
        }
      ]
    }
  },
  {
    id: "3",
    title: "AGZero - Redesign da Experiência Conversacional",
    slug: "agzero-redesign",
    company: "Take Blip / Banco Safra",
    role: "UX Writer & Product Designer",
    category: "UX Writing",
    description: "Reformulação completa da linguagem e fluxos do chatbot AGZero, elevando métricas de atendimento com comunicação intuitiva alinhada ao público jovem-adulto.",
    image: agzeroHeroImg,
    tags: ["UX Writing", "B2C", "Banking", "Mobile App", "Chatbot"],
    details: {
      challenge: "O AGZero, banco digital do Safra, enfrentava desafios críticos: NPS abaixo do esperado, alto volume de solicitações não resolvidas e demanda crescente por atendimento humanizado. Como oferecer uma experiência bancária intuitiva e descomplicada para o público jovem-adulto (20-39 anos)?",
      solution: "Realizamos um redesign completo da experiência conversacional, focando nas principais demandas: cartões, empréstimos, consultas e processos de encerramento. Mudamos a linguagem de 'posso ajudar' para 'posso descomplicar', refletindo uma transformação na forma de pensar o atendimento. Unificamos menus, implementamos tracking aprimorado e garantimos implementação ágil.",
      results: [
        "Unificação inteligente de menus relacionados",
        "Tracking aprimorado da jornada do usuário",
        "Redução significativa de solicitações não resolvidas",
        "Melhoria no NPS e satisfação do cliente",
        "Implementação ágil com entregas contínuas"
      ],
      responsibilities: [
        "Reformulação completa da linguagem do chatbot",
        "Redesign dos fluxos conversacionais",
        "Pesquisa e análise de dados de atendimento",
        "Prototipagem e testes de usabilidade",
        "Acompanhamento de métricas e otimização contínua"
      ],
      tools: ["Figma", "IBM Watson", "Take Blip", "Google Analytics"],
      keyLearning: "Este case demonstra como a união entre tecnologia e compreensão profunda do usuário pode transformar completamente a experiência bancária digital. Com sinergia e metodologia ágil, grandes transformações são possíveis mesmo com prazos desafiadores.",
      images: [
        {
          src: agzeroHeroImg,
          caption: "AGZero - Banco digital 100% sem complicações"
        },
        {
          src: agzeroIntencoesImg,
          caption: "Análise de intenções não reconhecidas para otimização do chatbot"
        },
        {
          src: agzeroMenuImg,
          caption: "Problema de não reconhecimento de menu e otimização proposta"
        },
        {
          src: agzeroAntesDepoisImg,
          caption: "Comparação: experiência atual vs proposta de onboarding"
        },
        {
          src: agzeroMinhaContaImg,
          caption: "Fluxo otimizado da seção Minha Conta"
        },
        {
          src: agzeroEncerramentoImg,
          caption: "Redesign do fluxo de encerramento de conta com empatia"
        },
        {
          src: agzeroPagamentoImg,
          caption: "Fluxo simplificado de pagamentos"
        },
        {
          src: agzeroServicosImg,
          caption: "Menu de serviços reorganizado por relevância"
        },
        {
          src: agzeroFluxogramaImg,
          caption: "Fluxograma completo do processo de finalização"
        }
      ]
    }
  }
];
